import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import type { AnnualMetrics } from '@/types/stock';
import { formatCurrency, formatPercent, formatNumber } from '@/lib/analysisEngine';

interface Props {
  metrics: AnnualMetrics[];
  metric: keyof AnnualMetrics;
  label: string;
  color?: string;
  showGrowthLine?: boolean;
  growthMetric?: keyof AnnualMetrics;
  isPercent?: boolean;
  isCurrency?: boolean;
  referenceValue?: number;
  referenceLabel?: string;
  height?: number;
}

function getMetricValue(m: AnnualMetrics, key: keyof AnnualMetrics): number | null {
  const v = m[key];
  if (typeof v === 'number') return v;
  return null;
}

const COLORS = {
  primary: '#3b82f6',
  growth: '#10b981',
  reference: '#f59e0b',
};

export function MetricTrendChart({
  metrics,
  metric,
  label,
  color = COLORS.primary,
  showGrowthLine = false,
  growthMetric,
  isPercent = false,
  isCurrency = false,
  referenceValue,
  referenceLabel,
  height = 260,
}: Props) {
  const data = metrics.map((m) => ({
    year: m.fiscalYear,
    value: getMetricValue(m, metric),
    growth: growthMetric ? getMetricValue(m, growthMetric) : null,
  }));

  const formatValue = (v: number) => {
    if (isPercent) return formatPercent(v);
    if (isCurrency) return formatCurrency(v);
    return formatNumber(v);
  };

  const CustomTooltip = ({ active, payload, label: lbl }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-foreground mb-1">{lbl}</p>
        {payload.map((entry) => (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {
              entry.name === 'YoY Growth'
                ? formatPercent(entry.value)
                : formatValue(entry.value)
            }
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
        {label}
      </h3>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
          <XAxis
            dataKey="year"
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tickFormatter={formatValue}
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          {showGrowthLine && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => formatPercent(v)}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
              width={50}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
          />
          {referenceValue !== undefined && (
            <ReferenceLine
              yAxisId="left"
              y={referenceValue}
              stroke={COLORS.reference}
              strokeDasharray="4 4"
              label={{ value: referenceLabel ?? `Target: ${formatValue(referenceValue)}`, position: 'right', fontSize: 10 }}
            />
          )}
          <Bar
            yAxisId="left"
            dataKey="value"
            name={label}
            fill={color}
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          />
          {showGrowthLine && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="growth"
              name="YoY Growth"
              stroke={COLORS.growth}
              strokeWidth={2}
              dot={{ r: 3, fill: COLORS.growth }}
              connectNulls={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
